import { Injectable, Injector, inject, Type, ComponentRef, ViewContainerRef } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { DynamicSheetComponent } from './../general/components/dynamic-sheet/dynamic-sheet';

@Injectable({ providedIn: 'root' })
export class SheetService {
  private bottomSheet = inject(MatBottomSheet);
  private injector = inject(Injector);

  /**
   * Opens a dynamic bottom sheet with the given component.
   * @param component The component class to render inside the sheet.
   * @param data Optional data to pass to the component instance (will be merged with close/onSave handlers).
   * @param config Optional configuration (e.g. title).
   * @returns Promise that resolves with the value passed to onSave, or undefined if dismissed without saving.
   */
  open<R = unknown>(
    component: Type<unknown>,
    data?: unknown,
    config?: { title?: string }
  ): Promise<R | undefined> {
    const sheetRef = this.bottomSheet.open(DynamicSheetComponent, {
      hasBackdrop: true,
      panelClass: 'full-screen-sheet',
      data: { title: config?.title || '' }
    });

    // Cast to access known properties of the dynamic sheet wrapper
    const sheet = sheetRef.instance as {
      title: string;
      content: ViewContainerRef;
    };

    sheet.title = config?.title || '';

    const compRef: ComponentRef<unknown> = sheet.content.createComponent(component, {
      injector: this.injector
    });

    // Inject close/onSave handlers into the dynamic component instance
    const instance = compRef.instance as { data?: unknown };
    instance.data = {
      ...(typeof data === 'object' && data !== null ? data : {}),
      close: () => sheetRef.dismiss(),
      onSave: (result: R) => sheetRef.dismiss(result)
    };

    return sheetRef.afterDismissed().toPromise() as Promise<R | undefined>;
  }
}
